/**
 * The canonical icon set.
 *
 * `DESIGN.md` closes the icon vocabulary the same way it closes the palette:
 * "An icon not on this list is a system change, not a local decision." Import
 * from here rather than from `lucide-react` directly, so adding one is a visible
 * edit to this file.
 *
 * lucide-react 1.x renamed five of the icons DESIGN.md names. The mapping is:
 *
 *   DESIGN.md          lucide-react 1.x
 *   ---------          ----------------
 *   alert-triangle  →  TriangleAlert
 *   alert-circle    →  CircleAlert
 *   check-circle-2  →  CircleCheckBig
 *   bar-chart-3     →  ChartColumn
 *   loader-2        →  LoaderCircle
 *   download-cloud  →  CloudDownload
 *
 * Every icon is stroke-only, `currentColor`, no fill, no icon font, no emoji.
 */
export {
  Building2,
  Calendar,
  Camera,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheckBig,
  Clock,
  CloudDownload,
  Delete,
  Download,
  Ellipsis,
  Eye,
  EyeOff,
  Info,
  Languages,
  LayoutGrid,
  ListFilter,
  LoaderCircle,
  Lock,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Minus,
  Moon,
  PanelLeft,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Settings2,
  Share2,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  TriangleAlert,
  Unlink,
  User,
  UserX,
  Users,
  Wifi,
  WifiOff,
  X,
} from 'lucide-react';

export type { LucideIcon, LucideProps } from 'lucide-react';

/**
 * Stroke weight by size, from DESIGN.md: 1.9 at 16–18 px, 2.0 at 20–26 px.
 * A lighter stroke at a larger size reads as a different icon family.
 */
export function iconStroke(sizePx: number): number {
  return sizePx >= 20 ? 2 : 1.9;
}

/** The sizes the system actually draws at. */
export const ICON_SIZE = {
  /** Inline with 11–13 px text: field errors, chip affordances. */
  xs: 14,
  /** Inside a control: buttons, toolbar, table cells. */
  sm: 16,
  /** Admin sidebar and most standalone icon buttons. */
  md: 18,
  /** Status bands, empty states. */
  lg: 20,
  /** Worker bottom navigation. */
  xl: 22,
  /** The punch key. */
  key: 26,
} as const;

export type IconSize = keyof typeof ICON_SIZE;

/**
 * Spread onto a lucide icon: correct size, correct stroke, and hidden from
 * assistive technology. Decorative is the default because every icon in this
 * product is supposed to sit beside a visible label — on the worker PWA that
 * is a hard rule, not a preference.
 */
export function decorativeIcon(size: IconSize = 'sm'): {
  size: number;
  strokeWidth: number;
  'aria-hidden': true;
  focusable: false;
} {
  const px = ICON_SIZE[size];
  return { size: px, strokeWidth: iconStroke(px), 'aria-hidden': true, focusable: false };
}
